import { FC, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Add } from '$icons/uncolored/Add.svg';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import RemoteSVG from '$components/RemoteSVG/RemoteSVG';

import AccountButton from '../AccountButton/AccountButton';
import DropdownMenu from '../DropdownMenu/DropdownMenu';
import AddNewJobModal from '../AddNewJobModal/AddNewJobModal';
import EmployerTermOfServiceModal from './EmployerTermOfService/EmployerTermOfServiceModal';
import CurrencySelector from './CurrencySelector/CurrencySelector';

export enum ActiveEmployerLayoutPageType {
  positions,
  teamMembers,
  addPosition,
}

interface HomeLayoutProps {
  children?: React.ReactNode;
  externalIsModalPositionOpen?: boolean | undefined;
  activePage: ActiveEmployerLayoutPageType;
}

const EmployerSPKLayout: FC<HomeLayoutProps> = ({
  children,
  externalIsModalPositionOpen,
  activePage,
}) => {
  const { t } = useTranslation('global');
  const { logoUrl } = useAppSelector((state) => state.brand);
  const navigate = useNavigate();

  const [openMenu, setOpenMenu] = useState(false);
  const [openTermOfService, setOpenTermOfService] = useState(false);
  const [isModalPositionOpen, setIsModalPositionOpen] = useState(false);

  const toggleIsModalPositionOpen = useCallback(
    () => setIsModalPositionOpen((prev) => !prev),
    []
  );

  const addPositionHandler = () => {
    navigate('/employer-dashboard/add-position');
  };

  useEffect(() => {
    if (externalIsModalPositionOpen) {
      setIsModalPositionOpen(true);
    }
  }, [externalIsModalPositionOpen]);

  const serverEmployer = JSON.parse(
    localStorage.getItem('ServerEmployerResponse') || '{}'
  );

  useEffect(() => {
    if (serverEmployer && serverEmployer.AgreedTermsOfServiceDate === null) {
      setOpenTermOfService(true);
    }
  }, [serverEmployer]);

  return (
    <div
      data-testid="employer-container"
      className="flex flex-col font-mulish min-h-screen bg-employer-background"
    >
      <nav className="flex flex-row bg-spk-neutral-10 py-2">
        <div className="container m-auto flex flex-row items-center px-2 md:p-0">
          <Link to="/employer-dashboard/positions">
            <RemoteSVG url={logoUrl} svgProps={{ className: 'h-6 w-auto' }} />
          </Link>
          <div className="ml-auto flex flex-row gap-8 items-center">
            <CurrencySelector />
            <Link
              to="/employer-dashboard/team-members"
              className={`${
                activePage === ActiveEmployerLayoutPageType.teamMembers
                  ? 'underline'
                  : 'text-employer-primary'
              } font-bold hidden md:flex`}
            >
              {t('EmployerDashboard.TeamMembers')}
            </Link>
            <Link
              className={`${
                activePage === ActiveEmployerLayoutPageType.positions
                  ? 'underline'
                  : 'text-employer-primary'
              } font-bold hidden md:flex`}
              to="/employer-dashboard/positions"
            >
              {t('EmployerDashboard.Positions')}
            </Link>
            <button
              type="button"
              onClick={addPositionHandler}
              className="text-employer-primary flex-row items-center rounded-full gap-4 bg-employer-primary/15 px-6 py-3 font-bold hidden md:flex"
            >
              <Add className="fill-employer-primary" />
              {t('EmployerDashboard.AddPosition')}
            </button>
            <div className="relative">
              <AccountButton
                isProfile
                t={t}
                action={() => setOpenMenu(!openMenu)}
                hideName
              />
              {openMenu && (
                <DropdownMenu
                  isEmployer
                  setModal={setOpenMenu}
                  onAddPosition={addPositionHandler}
                  className="!w-40 top-2 right-0"
                />
              )}
            </div>
          </div>
        </div>
      </nav>
      {children && (
        <div className="container mx-auto h-full flex flex-1">{children}</div>
      )}
      <AddNewJobModal
        onClose={toggleIsModalPositionOpen}
        modalIsOpen={isModalPositionOpen}
      />
      <EmployerTermOfServiceModal
        modalIsOpen={openTermOfService}
        employerId={serverEmployer?.Id}
        setOpenTermOfService={setOpenTermOfService}
      />
    </div>
  );
};

EmployerSPKLayout.defaultProps = {
  children: null,
  externalIsModalPositionOpen: undefined,
};

export default EmployerSPKLayout;
